import React, { useEffect, useState } from "react";
import axios from "axios";
import { Eye, Trash2, RotateCcw, Plus, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./Contract.css";

const url = process.env.REACT_APP_BACKEND_URL;

const ContractList = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const navigate = useNavigate();

  const fetchContracts = async () => {
    try {
      const endpoint = showInactive
        ? `${url}/api/contract/all`
        : `${url}/api/contract`;

      const response = await axios.get(endpoint);
      setContracts(response.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch contracts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [showInactive]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this contract?")) {
      try {
        await axios.delete(`${url}/api/contract/${id}`);
        toast.success("Contract deactivated successfully");
        fetchContracts();
      } catch (error) {
        toast.error("Failed to deactivate contract");
      }
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${url}/api/contract/${id}/restore`);
      toast.success("Contract restored successfully");
      fetchContracts();
    } catch (error) {
      toast.error("Failed to restore contract");
    }
  };

  const getStatusBadge = (status, contractStart, contractEnd) => {
    if (status === "inactive") {
      return <span className="status-badge inactive">Inactive</span>;
    }
    
    const today = new Date();
    const endDate = new Date(contractEnd);
    
    if (endDate < today) {
      return <span className="status-badge expired">Expired</span>;
    }
    
    return <span className="status-badge active">Active</span>;
  };

  if (loading) {
    return <div className="loading">Loading contracts...</div>;
  }

  return (
    <div className="contract-container">
      <div className="header">
        <h2>Contracts List</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="toggle-button"
          >
            {showInactive ? "Show Active Only" : "Show All"}
          </button>
          <button
            onClick={() => navigate("/AddContract")}
            className="add-button"
          >
            <Plus size={16} /> Add Contract
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="contract-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Customer</th>
              <th>Client</th>
              <th>Job Category</th>
              <th>Contract Period</th>
              <th>CID/Letter</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contracts.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No contracts found
                </td>
              </tr>
            ) : (
              contracts.map((contract) => (
                <tr key={contract._id}>
                  <td>{contract.no}</td>
                  <td>
                    <div className="customer-info">
                      <strong>{contract.cust_id?.fullname}</strong>
                      <small>{contract.cust_id?.phone}</small>
                    </div>
                  </td>
                  <td>
                    <div className="client-info">
                      <span>{contract.client_id?.name}</span>
                      <small>{contract.client_id?.location}</small>
                    </div>
                  </td>
                  <td>
                    <div className="job-info">
                      <span>{contract.job_id?.category_name}</span>
                      {contract.section && (
                        <small className="section-badge">{contract.section}</small>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="date-info">
                      <span>
                        {new Date(contract.contract_start).toLocaleDateString()} -{" "}
                        {new Date(contract.contract_end).toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td>
                    {contract.cid_letter_upload ? (
                      <a
                        href={`${url}${contract.cid_letter_upload}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="file-link"
                      >
                        View File
                      </a>
                    ) : (
                      <span className="no-file">No file</span>
                    )}
                  </td>
                  <td>
                    {getStatusBadge(contract.status, contract.contract_start, contract.contract_end)}
                  </td>
                  <td className="actions">
                    <button
                      onClick={() => navigate(`/contract/view/${contract._id}`)}
                      className="view-btn"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => navigate(`/contract/report/${contract._id}`)}
                      className="report-btn"
                      title="View Report"
                    >
                      <FileText size={16} />
                    </button>
                    {contract.status === "active" ? (
                      <button
                        onClick={() => handleDelete(contract._id)}
                        className="delete-btn"
                        title="Deactivate"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRestore(contract._id)}
                        className="restore-btn"
                        title="Restore"
                      >
                        <RotateCcw size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContractList;